import { AxiosError } from 'axios'
import { signOut } from 'next-auth/react'
import { useContext } from 'react'
import { useErrorBoundary } from 'react-error-boundary'
import { FieldError, FieldValues, Path, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { getErrorMessageKey } from '@/constants/errorCodeMapping'
import { SnackbarContext } from '@/contexts/Snackbar'
import { isHttpStatusError } from '@/utils/apiUtils'

const CLIENT_CANCELED = [AxiosError.ERR_CANCELED, AxiosError.ECONNABORTED]

type FormErrors<FormData extends FieldValues> = Record<
  Path<FormData>,
  FieldError
>

type ErrorResponseData = { code?: string } | undefined

type AxiosErrorResponse = {
  status: number
  data: ErrorResponseData
}

/**
 * APIエラーレスポンスを処理してメッセージを取得する
 * @param response エラーレスポンス全体
 * @param t 翻訳関数
 * @returns 表示するメッセージ
 */
const handleErrorResponse = (
  response: AxiosErrorResponse,
  t: (key: string) => string,
): string => {
  const errorCode = response.data?.code

  // エラーコードマッピングを使用してメッセージキーを取得
  const messageKey = errorCode
    ? getErrorMessageKey(response.status, errorCode)
    : undefined

  return messageKey && t(messageKey) !== messageKey
    ? t(messageKey)
    : t('unexpected_error_has_occurred')
}

export const useHandleAxiosError = <FormData extends FieldValues>(
  props: { form?: UseFormReturn<FormData> } = {},
) => {
  const { t } = useTranslation()
  const { form } = props
  const { showBoundary } = useErrorBoundary()
  const { snackbarDispatch } = useContext(SnackbarContext)

  /** API Responseを受け取って異常 or 準正常を判別し、それぞれ処理する */
  const handleAxiosError = (err: unknown) => {
    // キャンセル系は無視
    if (err instanceof AxiosError && CLIENT_CANCELED.includes(err.code ?? '')) {
      return undefined
    }

    // 異常系エラー
    if (
      !(err instanceof AxiosError) ||
      isHttpStatusError(err.response?.status)
    ) {
      showBoundary(err) // ErrorBoundaryで処理
    }
    // 認証エラー
    else if (err.response?.status === 401) {
      signOut()
    }
    // 409 Conflict エラー
    else if (err.response?.status === 409) {
      snackbarDispatch({
        message: handleErrorResponse(err.response, t),
        severity: 'error',
      })
    }
    // 準正常系エラー
    else {
      // バリデーションエラーをフォームにバインドする
      if (form && typeof err.response?.data === 'object') {
        const fieldValues = form.getValues()
        const validationErrors: FormErrors<FormData> =
          {} as FormErrors<FormData>
        Object.entries(err.response.data).forEach(([name, messages]) => {
          // フォームに存在するフィールドのみに絞る
          if (name in fieldValues) {
            validationErrors[name as Path<FormData>] = {
              type: 'manual',
              message: String(messages),
            }
          } else {
            // TODO: フォームに無いフィールドのエラーはどう扱うべきか？
          }
        })
      }

      const { detail } = err.response?.data || 'error'

      snackbarDispatch({
        message: detail,
        severity: 'error',
      })
    }
    return undefined
  }

  return { handleAxiosError }
}
